import React, { useRef, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Snackbar,
  Alert,
} from '@mui/material';
import GenericModal from './GenericModal';
import UpcomingDateEntryField from './UpcomingDateEntryField';
import { datedChecklistService, recurringItemService } from '../services/api';
import { buildUpcomingDateValue, MONTH_MAP, validateUpcomingDateParts } from '../utils/upcomingDate';
import { useIsCoarsePointer } from '../hooks/useMobileKeyboardVisible';

interface AddUpcomingModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: (date: string, alreadyExists?: boolean) => void;
}

const AddUpcomingModal: React.FC<AddUpcomingModalProps> = ({ open, onClose, onSuccess }) => {
  const currentYear = new Date().getFullYear();
  
  const [day, setDay] = useState('');
  const [month, setMonth] = useState('');
  const [year, setYear] = useState(currentYear.toString());
  const [itemsText, setItemsText] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('error');
  const [dayError, setDayError] = useState('');
  const [monthError, setMonthError] = useState('');
  const [yearError, setYearError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isCoarsePointer = useIsCoarsePointer();
  const isSubmitInFlightRef = useRef(false);
  const formContainerRef = useRef<HTMLDivElement>(null);

  const handleAdd = async () => {
    if (isSubmitInFlightRef.current || isSubmitting) {
      return;
    }

    // Validate all fields
    if (!day || !month || !year) {
      setSnackbarMessage('Please fill in all date fields');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    if (dayError || monthError || yearError) {
      setSnackbarMessage('Please fix validation errors');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    if (month.length !== 3 || !Object.keys(MONTH_MAP).includes(month)) {
      setSnackbarMessage('Please enter a valid 3-letter month (JAN, FEB, etc.)');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    isSubmitInFlightRef.current = true;
    setIsSubmitting(true);
    setSnackbarOpen(false); // Close any previous snackbar messages

    try {
      // Format date as YYYY-MM-DD
      const dateResult = buildUpcomingDateValue({ day, month, year });
      if (!dateResult.date) {
        setSnackbarMessage(dateResult.error || 'Invalid date');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        return;
      }

      const date = dateResult.date;

      // Format title as "Mon DD, YYYY"
      const dateObj = new Date(date + 'T00:00:00');
      const title = dateObj.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric' 
      });

      // Parse newline-separated items (optional)
      const initialItems = itemsText
        .split('\n')
        .map(item => item.trim())
        .filter(item => item.length > 0);

      // Create the checklist with optional initial items
      const response = await datedChecklistService.createChecklist({ 
        date, 
        title,
        ...(initialItems.length > 0 && { initialItems })
      });

      if (!response.success) {
        throw new Error(response.error || 'Failed to create checklist');
      }

      // Check if the date already existed
      const dateAlreadyExisted = response.code === 'CHECKLIST_ALREADY_EXISTS';

      // If creating a new date (not already existed), check for recurring items
      if (!dateAlreadyExisted) {
        const recurringItemsResponse = await recurringItemService.getRecurringItemsForDate(date);
        
        if (recurringItemsResponse.success && recurringItemsResponse.data && recurringItemsResponse.data.length > 0) {
          // Add each recurring item to the newly created date
          for (const recurringItem of recurringItemsResponse.data) {
            const createItemResponse = await datedChecklistService.createItem(date, { 
              text: recurringItem.text,
              recurringMetadataId: recurringItem.id,
            });
            
            // If the recurring item has a custom color and the item was created successfully, update the color
            if (createItemResponse.success && createItemResponse.data && recurringItem.customColor) {
              await datedChecklistService.updateItemColor(date, createItemResponse.data.id, recurringItem.customColor);
            }
          }
        }
      }

      // Reset form
      setDay('');
      setMonth('');
      setYear(currentYear.toString());
      setItemsText('');
      setDayError('');
      setMonthError('');
      setYearError('');

      // Close modal immediately and trigger success callback
      onClose();
      if (onSuccess) {
        onSuccess(date, dateAlreadyExisted);
      }
    } catch (error) {
      setSnackbarMessage(error instanceof Error ? error.message : 'Failed to create checklist');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      isSubmitInFlightRef.current = false;
      setIsSubmitting(false);
    }
  };

  const handleItemsTextBlur = (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (!isCoarsePointer || isSubmitting) {
      return;
    }

    const relatedTarget = event.relatedTarget as HTMLElement | null;
    if (relatedTarget && formContainerRef.current?.contains(relatedTarget)) {
      return;
    }

    window.requestAnimationFrame(() => {
      void handleAdd();
    });
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleDayChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const validation = validateUpcomingDateParts({ day: value, month, year }, currentYear);
    setDay(validation.parts.day);
    setDayError(validation.errors.day);
  };

  const handleMonthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toUpperCase();
    const validation = validateUpcomingDateParts({ day, month: value, year }, currentYear);
    setMonth(validation.parts.month);
    setMonthError(validation.errors.month);
  };

  const handleYearChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const validation = validateUpcomingDateParts({ day, month, year: value }, currentYear);
    setYear(validation.parts.year);
    setYearError(validation.errors.year);
  };

  return (
    <>
      <GenericModal
        open={open}
        onClose={onClose}
        maxWidth={600}
        ariaLabelledBy="add-upcoming-modal"
        ariaDescribedBy="add-upcoming-description"
      >
        <Box ref={formContainerRef} sx={{ pt: 2 }}>
          {/* Date Fields in Large Text */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 2,
              mb: 4,
            }}
          >
            <UpcomingDateEntryField
              day={day}
              month={month}
              year={year}
              errors={{ day: dayError, month: monthError, year: yearError }}
              onDayChange={handleDayChange}
              onMonthChange={handleMonthChange}
              onYearChange={handleYearChange}
            />
          </Box>

          {/* Items Field */}
          <Box sx={{ mb: 3, mt: 4 }}>
            <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
              Items
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              value={itemsText}
              onChange={(e) => setItemsText(e.target.value)}
              onBlur={handleItemsTextBlur}
              placeholder="Newline-separated items..."
              variant="outlined"
            />
          </Box>

          {/* Add Button */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <Button
              variant="contained"
              size="large"
              onClick={handleAdd}
              disabled={isSubmitting}
              sx={{
                px: 6,
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 600,
              }}
            >
              {isSubmitting ? 'Adding...' : 'Add'}
            </Button>
          </Box>
        </Box>
      </GenericModal>

      {/* Snackbar for feedback */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AddUpcomingModal;
